import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import MobileLayout from "../../components/layout/MobileLayout";
import { api } from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { toast } from "sonner";
import { ChevronRight } from "lucide-react";
import ConfirmBidsModal from "../../components/ConfirmBidsModal";
import BetWarningModal from "../../components/BetWarningModal";

const QUICK_AMOUNTS = [10, 50, 100, 500, 1000];

export default function JodiBetPage({ embedded = false }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, refresh } = useAuth();
  const [market, setMarket] = useState(null);
  const [settings, setSettings] = useState({});
  const [bets, setBets] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [quickAmt, setQuickAmt] = useState(0); // 0 = no chip selected → inline typing per tile
  const [showReview, setShowReview] = useState(false);
  const [warn, setWarn] = useState(null);

  useEffect(() => {
    api.get(`/markets/${id}`).then(m => setMarket(m.data));
    api.get("/settings").then(({ data }) => setSettings(data)).catch(() => {});
  }, [id]);

  const total = useMemo(() => Object.values(bets).reduce((s, v) => s + (Number(v) || 0), 0), [bets]);
  const minBet = settings.min_bet_jodi ?? 10;
  const numbers = useMemo(() => Array.from({ length: 100 }, (_, i) => i.toString().padStart(2, "0")), []);

  function setAmt(num, val) {
    const raw = String(val).replace(/\D/g, "").slice(0, 6);
    const n = Number(raw) || 0;
    setBets(prev => { const next = { ...prev }; if (n > 0) next[num] = n; else delete next[num]; return next; });
  }
  function tapTile(num) {
    // If a quick chip is active → add that amount. Otherwise, focus happens via input.
    if (quickAmt > 0) {
      setBets(prev => ({ ...prev, [num]: (Number(prev[num]) || 0) + quickAmt }));
    }
  }
  function clearKey(k) {
    setBets(prev => { const next = { ...prev }; delete next[k]; return next; });
  }

  function openReview() {
    if (!market?.is_market_active) {
      setWarn({ type: "closed", title: "मार्केट बंद है", message: "अभी इस मार्केट पर बोली नहीं लगाई जा सकती।" });
      return;
    }
    if (total <= 0) {
      setWarn({ type: "empty", title: "कोई जोड़ी चुनें", message: "पहले किसी जोड़ी नंबर के सामने अमाउंट डालें।" });
      return;
    }
    if (total < minBet) {
      setWarn({ type: "min", title: "अमाउंट बहुत कम है", message: `जोड़ी के लिए मिनिमम बोली ₹${minBet} है।`, hint: `अभी आपका टोटल ₹${total} है` });
      return;
    }
    if (total > (user?.wallet_balance || 0)) {
      setWarn({ type: "insufficient", title: "वॉलेट में बैलेंस कम है", message: `वॉलेट में सिर्फ़ ₹${Number(user?.wallet_balance || 0).toFixed(2)} है।`, hint: `बोली का टोटल ₹${total}` });
      return;
    }
    setShowReview(true);
  }

  async function confirmPlace() {
    const payload = {
      market_id: market.id,
      bids: Object.entries(bets).filter(([_, v]) => Number(v) > 0)
        .map(([num, amt]) => ({ game_type: "jodi", session: null, number: num, amount: Number(amt) })),
    };
    try {
      setSubmitting(true);
      await api.post("/bids", payload);
      await refresh();
      setShowReview(false);
      toast.success(`✅ ${payload.bids.length} जोड़ी बोली लगी (कुल ₹${total})`);
      navigate("/my-bids");
    } catch (e) { toast.error(e?.response?.data?.detail || "बोली नहीं लगी"); }
    finally { setSubmitting(false); }
  }

  const rows = Object.entries(bets).sort((a, b) => a[0].localeCompare(b[0]))
    .map(([num, amt]) => ({ key: num, label: `जोड़ी: ${num}`, amount: Number(amt) }));

  return (
    <Wrap embedded={embedded}>
      {/* Quick chip strip (optional) */}
      <QuickChipStrip
        value={quickAmt}
        onChange={setQuickAmt}
        presets={QUICK_AMOUNTS}
        testIdPrefix="jodi"
        hint="चिप दबाकर टाइल पर टैप करें • या नीचे हर नंबर के सामने अपना amount लिखें"
      />

      {/* 00-99 grid with inline amount input per tile */}
      <div className="grid grid-cols-5 gap-2 pb-28" data-testid="jodi-grid">
        {numbers.map(n => {
          const amt = bets[n] || 0;
          const active = amt > 0;
          return (
            <div
              key={n}
              onClick={() => tapTile(n)}
              data-testid={`jodi-num-${n}`}
              className={`relative rounded-xl p-2 flex flex-col items-center transition ${
                active
                  ? "border-2 border-cyan-400 bg-cyan-400/5 shadow-[0_0_14px_rgba(56,189,248,0.45)]"
                  : "border border-cyan-400/15 bg-black/40"
              }`}
            >
              <span className={`font-display font-black text-lg leading-tight ${active ? "text-cyan-200" : "text-white"}`}>
                {n}
              </span>
              <input
                type="text"
                inputMode="numeric"
                value={amt > 0 ? String(amt) : ""}
                onChange={(e) => setAmt(n, e.target.value)}
                onClick={(e) => e.stopPropagation()}
                data-testid={`jodi-amt-${n}`}
                placeholder="₹"
                className={`mt-1.5 w-full h-7 rounded-md text-center text-sm font-black tabular-nums bg-transparent focus:outline-none placeholder:text-slate-600 ${
                  active ? "border border-cyan-400/60 text-cyan-200 bg-cyan-400/5" : "border border-slate-700 text-slate-300"
                }`}
              />
            </div>
          );
        })}
      </div>

      <BottomBar
        label="कुल पॉइंट्स"
        value={total}
        onProceed={openReview}
        canProceed={total > 0 && market?.is_market_active}
      />

      <ConfirmBidsModal
        open={showReview}
        rows={rows}
        total={total}
        onClose={() => setShowReview(false)}
        onConfirm={confirmPlace}
        onDelete={clearKey}
        submitting={submitting}
      />

      <BetWarningModal
        open={!!warn}
        type={warn?.type}
        title={warn?.title}
        message={warn?.message}
        hint={warn?.hint}
        onClose={() => setWarn(null)}
      />
    </Wrap>
  );
}

function Wrap({ embedded, children }) {
  if (embedded) return <>{children}</>;
  return <MobileLayout hideBottomNav hideHeader><div className="px-4 pt-4">{children}</div></MobileLayout>;
}

/** Shared quick-chip strip (tap to arm "add on tile click"). */
export function QuickChipStrip({ value, onChange, presets, testIdPrefix, hint }) {
  const active = Number(value) || 0;
  return (
    <div
      className="rounded-2xl border border-cyan-400/20 bg-black/40 backdrop-blur-sm p-3 mb-3"
      data-testid={`${testIdPrefix}-quick-strip`}
    >
      <div className="text-[11px] font-black tracking-[0.25em] text-cyan-300 text-center mb-2">
        क्विक चिप (टैप करके जोड़ें)
      </div>
      <div className="grid grid-cols-5 gap-2">
        {presets.map(v => {
          const on = active === v;
          return (
            <button
              key={v}
              onClick={() => onChange(on ? 0 : v)}
              data-testid={`${testIdPrefix}-quick-${v}`}
              className={`h-10 rounded-xl font-black text-sm transition active:scale-95 ${
                on
                  ? "text-cyan-200 border-2 border-cyan-400 bg-cyan-400/10 shadow-[0_0_12px_rgba(56,189,248,0.5)]"
                  : "text-slate-300 border border-cyan-400/20 bg-black/40"
              }`}
            >
              ₹{v}
            </button>
          );
        })}
      </div>
      {hint && (
        <div className="mt-2 text-[10px] text-slate-500 text-center leading-snug">
          {hint}
        </div>
      )}
    </div>
  );
}

export function BottomBar({ label, value, onProceed, canProceed, extraRight = null }) {
  return (
    <div
      className="fixed bottom-0 left-1/2 -translate-x-1/2 w-full max-w-md z-40 px-4 py-3"
      style={{ background: "linear-gradient(180deg, rgba(6,14,27,0) 0%, rgba(6,14,27,0.9) 30%, rgba(4,8,18,1) 100%)" }}
      data-testid="bidding-bottom-bar"
    >
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0">
          <div className="text-[10px] font-black tracking-[0.25em] text-slate-400">{label}</div>
          <div className="font-display font-black text-cyan-300 text-xl tabular-nums leading-tight" data-testid="bidding-total">
            ₹ {value}
          </div>
        </div>
        {extraRight}
        <button
          onClick={onProceed}
          disabled={!canProceed}
          data-testid="bidding-proceed"
          className={`h-12 px-6 rounded-full font-black text-white tracking-widest text-sm inline-flex items-center justify-center gap-1.5 transition active:scale-95 ${
            canProceed ? "shadow-[0_10px_30px_-10px_rgba(56,189,248,0.7)]" : "opacity-40"
          }`}
          style={{ background: canProceed ? "linear-gradient(135deg,#22d3ee 0%,#2563eb 100%)" : "#475569" }}
        >
          PROCEED <ChevronRight className="w-5 h-5" strokeWidth={2.6} />
        </button>
      </div>
    </div>
  );
}
